import axiosInstance from '../../../config/axios';

export type PartnerKind = 'CUSTOMER' | 'PARTNER' | 'SUPPLIER';
export type LifecycleStage = 'LEAD' | 'PROSPECT' | 'ACTIVE' | 'INACTIVE';

export interface BusinessPartner {
  id: number;
  kind: PartnerKind;
  lifecycle_stage: LifecycleStage;
  is_company: boolean;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  house_no?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  tax_number?: string;
  vat_id?: string;
  notes?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateBusinessPartnerPayload {
  kind: PartnerKind;
  lifecycle_stage?: LifecycleStage;
  is_company: boolean;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  house_no?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  tax_number?: string;
  vat_id?: string;
  notes?: string;
}

export interface UpdateBusinessPartnerPayload extends Partial<CreateBusinessPartnerPayload> {
  active?: boolean;
}

export interface BusinessPartnerListParams {
  kind?: PartnerKind;
  lifecycle_stage?: string;
  search?: string;
  active?: boolean;
}

const BASE = '/api/admin/business-partners/';

function unwrap<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default class BusinessPartnerService {
  static async list(params?: BusinessPartnerListParams): Promise<BusinessPartner[]> {
    const cleanParams = params
      ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== ''))
      : {};
    const { data } = await axiosInstance.get(BASE, { params: cleanParams });
    return unwrap<BusinessPartner>(data);
  }

  static async get(id: number): Promise<BusinessPartner> {
    const { data } = await axiosInstance.get(`${BASE}${id}/`);
    return data as BusinessPartner;
  }

  static async create(payload: CreateBusinessPartnerPayload): Promise<BusinessPartner> {
    const { data } = await axiosInstance.post(BASE, payload);
    return data as BusinessPartner;
  }

  static async update(id: number, payload: UpdateBusinessPartnerPayload): Promise<BusinessPartner> {
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined)
    );
    const { data } = await axiosInstance.patch(`${BASE}${id}/`, cleanPayload);
    return data as BusinessPartner;
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static getDisplayName(partner: BusinessPartner): string {
    if (partner.is_company && partner.company_name) {
      return partner.company_name;
    }
    const parts = [partner.last_name, partner.first_name].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Unnamed';
  }

  static getKindLabel(kind: PartnerKind): string {
    switch (kind) {
      case 'CUSTOMER':
        return 'Customer';
      case 'PARTNER':
        return 'Partner';
      case 'SUPPLIER':
        return 'Supplier';
      default:
        return kind;
    }
  }

  static getStageLabel(stage: LifecycleStage): string {
    switch (stage) {
      case 'LEAD':
        return 'Lead';
      case 'PROSPECT':
        return 'Prospect';
      case 'ACTIVE':
        return 'Active';
      case 'INACTIVE':
        return 'Inactive';
      default:
        return stage;
    }
  }
}
